import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Shield, Users, CreditCard, Activity, Mail, Building, Receipt } from 'lucide-react'
import { Link } from 'react-router-dom'
import { adminAPI } from '../../services/api'

const AdminDashboard: React.FC = () => {
  const { t } = useTranslation()
  const [stats, setStats] = useState({ activeUsers: '--', activeLicenses: '--' })
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchStats()
  }, [])

  const fetchStats = async () => {
    try {
      const response = await adminAPI.getStats()
      setStats({
        activeUsers: response.data.data.totalUsers || '--',
        activeLicenses: response.data.data.activeLicenses || '--'
      })
    } catch (error) {
      console.error('Error fetching stats:', error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-white mb-2">{t('admin.dashboard')}</h1>
        <p className="text-gray-400">Panel administracyjny PresentClic</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Link
          to="/admin/users"
          className="bg-dark-800 rounded-lg p-6 border border-dark-700 hover:border-primary/50 transition-colors group"
        >
          <div className="flex items-center mb-4">
            <div className="p-3 bg-blue-600/10 rounded-lg group-hover:bg-blue-600/20 transition-colors">
              <Users className="h-8 w-8 text-blue-400" />
            </div>
            <div className="ml-4">
              <h3 className="text-xl font-semibold text-white">{t('admin.users')}</h3>
              <p className="text-gray-400">Zarządzaj kontami użytkowników</p>
            </div>
          </div>
        </Link>

        <Link
          to="/admin/licenses"
          className="bg-dark-800 rounded-lg p-6 border border-dark-700 hover:border-primary/50 transition-colors group"
        >
          <div className="flex items-center mb-4">
            <div className="p-3 bg-primary/10 rounded-lg group-hover:bg-primary/20 transition-colors">
              <CreditCard className="h-8 w-8 text-primary" />
            </div>
            <div className="ml-4">
              <h3 className="text-xl font-semibold text-white">{t('admin.licenses')}</h3>
              <p className="text-gray-400">Zarządzaj licencjami użytkowników</p>
            </div>
          </div>
        </Link>

        <Link
          to="/admin/payments"
          className="bg-dark-800 rounded-lg p-6 border border-dark-700 hover:border-primary/50 transition-colors group"
        >
          <div className="flex items-center mb-4">
            <div className="p-3 bg-green-600/10 rounded-lg group-hover:bg-green-600/20 transition-colors">
              <Receipt className="h-8 w-8 text-green-400" />
            </div>
            <div className="ml-4">
              <h3 className="text-xl font-semibold text-white">Płatności</h3>
              <p className="text-gray-400">Zarządzaj płatnościami i statystykami</p>
            </div>
          </div>
        </Link>

        <div className="bg-dark-800 rounded-lg p-6 border border-dark-700">
          <div className="flex items-center mb-4">
            <div className="p-3 bg-blue-600/10 rounded-lg">
              <Activity className="h-8 w-8 text-blue-400" />
            </div>
            <div className="ml-4">
              <h3 className="text-xl font-semibold text-white">Statystyki</h3>
              <p className="text-gray-400">Przegląd aktywności systemu</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 mt-4">
            <div>
              <p className="text-sm text-gray-400">Aktywni użytkownicy</p>
              <p className="text-2xl font-bold text-white">
                {loading ? '...' : stats.activeUsers}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-400">Aktywne licencje</p>
              <p className="text-2xl font-bold text-white">
                {loading ? '...' : stats.activeLicenses}
              </p>
            </div>
          </div>
        </div>

        <Link
          to="/admin/security"
          className="bg-dark-800 rounded-lg p-6 border border-dark-700 hover:border-primary/50 transition-colors group"
        >
          <div className="flex items-center mb-4">
            <div className="p-3 bg-red-600/10 rounded-lg group-hover:bg-red-600/20 transition-colors">
              <Shield className="h-8 w-8 text-red-400" />
            </div>
            <div className="ml-4">
              <h3 className="text-xl font-semibold text-white">Zabezpieczenia Real-Time</h3>
              <p className="text-gray-400">Monitorowanie aktywności użytkowników</p>
            </div>
          </div>
        </Link>

        <Link
          to="/admin/email-settings"
          className="bg-dark-800 rounded-lg p-6 border border-dark-700 hover:border-primary/50 transition-colors group"
        >
          <div className="flex items-center mb-4">
            <div className="p-3 bg-orange-600/10 rounded-lg group-hover:bg-orange-600/20 transition-colors">
              <Mail className="h-8 w-8 text-orange-400" />
            </div>
            <div className="ml-4">
              <h3 className="text-xl font-semibold text-white">Ustawienia Poczty</h3>
              <p className="text-gray-400">Konfiguracja serwera email</p>
            </div>
          </div>
        </Link>

        <Link
          to="/admin/company-settings"
          className="bg-dark-800 rounded-lg p-6 border border-dark-700 hover:border-primary/50 transition-colors group"
        >
          <div className="flex items-center mb-4">
            <div className="p-3 bg-teal-600/10 rounded-lg group-hover:bg-teal-600/20 transition-colors">
              <Building className="h-8 w-8 text-teal-400" />
            </div>
            <div className="ml-4">
              <h3 className="text-xl font-semibold text-white">Dane Firmy</h3>
              <p className="text-gray-400">Dane do faktur i dokumentów</p>
            </div>
          </div>
        </Link>
      </div>
    </div>
  )
}

export default AdminDashboard